import React, { useState } from "react";
import { Upload, X, Loader2, FileText, ExternalLink } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";
import { uploadFile, fileUrl } from "../api";

// Format a raw string into DD/MM/AAAA as the user types digits
function maskDate(raw) {
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  const parts = [];
  if (digits.length >= 2) {
    parts.push(digits.slice(0, 2));
    if (digits.length >= 4) {
      parts.push(digits.slice(2, 4));
      parts.push(digits.slice(4));
    } else {
      parts.push(digits.slice(2));
    }
  } else {
    parts.push(digits);
  }
  return parts.filter((p) => p !== "").join("/");
}

export default function FormField({ field, value, error, onChange }) {
  const [uploading, setUploading] = useState(false);

  const labelEl = field.type !== "checkbox" && (
    <Label className="text-sm font-medium text-slate-700">
      {field.label}
      {field.required && <span className="ml-0.5 text-[#465EFF]">*</span>}
    </Label>
  );

  const errorEl = error && (
    <p className="text-xs font-medium text-red-500">{error}</p>
  );
  const hintEl = field.hint && !error && (
    <p className="text-xs text-slate-400">{field.hint}</p>
  );

  const wrapperCls = field.fullWidth ? "sm:col-span-2" : "";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const isImg = file.type.startsWith("image/");
      const res = await uploadFile(file);
      onChange({ url: res.url, name: res.name || file.name, isImage: res.isImage ?? isImg });
    } catch {
      onChange(null);
    } finally {
      setUploading(false);
    }
  };

  // ---- Render by type ----
  if (field.type === "checkbox") {
    return (
      <div className={cn("flex flex-col gap-1", wrapperCls)}>
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <Checkbox
            id={field.name}
            checked={!!value}
            onCheckedChange={(v) => onChange(!!v)}
            className="mt-0.5 data-[state=checked]:bg-[#465EFF] data-[state=checked]:border-[#465EFF]"
          />
          <Label htmlFor={field.name} className="cursor-pointer text-sm font-normal leading-snug text-slate-600">
            {field.label}
            {field.required && <span className="ml-0.5 text-[#465EFF]">*</span>}
          </Label>
        </div>
        {errorEl}
      </div>
    );
  }

  if (field.type === "file") {
    const val = value && typeof value === "object" ? value : null;
    return (
      <div className={cn("flex flex-col gap-1.5", wrapperCls)}>
        {labelEl}
        <div className="flex flex-wrap items-center gap-3">
          <label className={cn(
            "flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-500 transition-colors hover:border-[#465EFF] hover:text-[#465EFF]",
            uploading && "pointer-events-none opacity-60"
          )}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Enviando..." : "Selecionar arquivo"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
          </label>
          {val && (
            <div className="relative flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 pr-8">
              {val.isImage ? (
                <img src={fileUrl(val.url)} alt="" className="h-12 w-12 rounded object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded bg-[#465EFF]/10">
                  <FileText className="h-6 w-6 text-[#465EFF]" />
                </div>
              )}
              <a
                href={fileUrl(val.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex max-w-[160px] items-center gap-1 truncate text-xs font-medium text-[#465EFF] hover:underline"
              >
                <span className="truncate">{val.name || "arquivo"}</span>
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        {hintEl}
        {errorEl}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className={cn("flex flex-col gap-1.5", wrapperCls)}>
        {labelEl}
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className={cn("bg-white", error && "border-red-400")}>
            <SelectValue placeholder="Selecione..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hintEl}
        {errorEl}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className={cn("flex flex-col gap-1.5", wrapperCls)}>
        {labelEl}
        <Textarea
          value={value || ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn("bg-white", error && "border-red-400")}
          rows={3}
        />
        {hintEl}
        {errorEl}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1.5", wrapperCls)}>
      {labelEl}
      <Input
        type="text"
        inputMode={field.mask === "date" ? "numeric" : undefined}
        value={value || ""}
        placeholder={field.placeholder}
        onChange={(e) => onChange(field.mask === "date" ? maskDate(e.target.value) : e.target.value)}
        className={cn("bg-white", error && "border-red-400")}
      />
      {hintEl}
      {errorEl}
    </div>
  );
}
